import React, { useEffect, useState } from 'react';
import {
    Button,
    Divider,
    Feed,
    Header,
    Icon,
    Modal,
    Tab,
    Table,
} from 'semantic-ui-react';
import axios from 'axios';
import moment from 'moment';

import UserDetailConfig from './UserDetailConfig';
import { useError } from '../providers';

const UserDetail = ({ user }) => {
    const [open, setOpen] = useState(false);
    const [commands, setCommands] = useState([]);
    const [donationConfig, setDonationConfig] = useState({});
    const [channelPoints, setChannelPoints] = useState([]);
    const [twitchPermissions, setTwitchPermissions] = useState({});

    const { showError } = useError();

    const startImpersonation = async () => {
        try {
            await axios(`/api/admin/impersonate/${user.twitch_name}`,);
            window.location.reload();
        } catch (err) {
            showError(<center>Oops, something went wrong. Don't worry, we've been notified of the issue and will fix it as soon as possible!</center>);
        }
    }

    useEffect(() => {
        const getCommands = async () => {
            try {
                const { data } = await axios(
                    `/api/commands/${user.twitch_name}`,
                );
                if (data.status === 200) {
                    setCommands(data.response.commands || []);
                }
            } catch (err) {
                showError(<center>Oops, something went wrong. Don't worry, we've been notified of the issue and will fix it as soon as possible!</center>);
            }
        }

        const getDonationConfig = async () => {
            try {
                const { data } = await axios(
                    `/api/admin/donation/${user.twitch_name}`
                );

                if (data.status === 200) {
                    setDonationConfig(data.response || {})
                }
            } catch (err) {
                showError(<center>Oops, something went wrong. Don't worry, we've been notified of the issue and will fix it as soon as possible!</center>);
            }
        }

        const getChannelPoints = async () => {
            try {
                const { data } = await axios(
                    `/api/admin/channelpoints/${user.twitch_name}`,
                );

                if (data.status === 200) {
                    setChannelPoints(data.response.redeems || []);
                }
            } catch (err) {
                showError(<center>Oops, something went wrong. Don't worry, we've been notified of the issue and will fix it as soon as possible!</center>);
            }
        }

        const getTwitchPermissions = async () => {
            try {
                const { data } = await axios(
                    `/api/admin/perms/${user.twitch_name}`,
                );

                setTwitchPermissions(data);
            } catch (err) {
                showError(<center>Oops, something went wrong. Don't worry, we've been notified of the issue and will fix it as soon as possible!</center>);
            }
        }

        if (open) {
            getCommands();
            getDonationConfig();
            getChannelPoints();
            getTwitchPermissions();
        }
    }, [showError, user, open]);

    const panes = [
        {
            menuItem: 'Commands',
            render: () => (
                <Tab.Pane inverted style={{ padding: '0px', overflowX: 'hidden', overflowY: 'overlay', maxHeight: '350px' }}>
                    <Table inverted singleLine striped unstackable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>
                                    Command
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    Enabled
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    Sub Only
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    Custom
                                </Table.HeaderCell>
                                <Table.HeaderCell colSpan={2}>
                                    Rate Limit
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {Object.values(commands).length === 0 ?
                                <Table.Row>
                                    <Table.Cell colSpan={6}>
                                        <Header textAlign='center' style={{ 'color': 'white' }}>
                                            No commands available.
                                        </Header>
                                    </Table.Cell>
                                </Table.Row>
                                :
                                Object.values(commands).map((command, index) =>
                                    <Table.Row key={index}>
                                        <Table.Cell>
                                            <strong>
                                                {command.syntax}
                                            </strong>
                                            {command.command !== command.original ? (
                                                <React.Fragment>
                                                    <br />
                                                    <em>Aliased from {command.original}.</em>
                                                </React.Fragment>
                                            ) : ''}
                                        </Table.Cell>
                                        <Table.Cell collapsing textAlign='center'>
                                            {command.flags & 1 ?
                                                <Icon name='check' />
                                                :
                                                <Icon name='x' />
                                            }
                                        </Table.Cell>
                                        <Table.Cell collapsing textAlign='center'>
                                            {command.flags & 2 ?
                                                <Icon name='check' />
                                                :
                                                <Icon name='x' />
                                            }
                                        </Table.Cell>
                                        <Table.Cell collapsing textAlign='center'>
                                            {command.is_custom ?
                                                <Icon name='check' />
                                                :
                                                <Icon name='x' />
                                            }
                                        </Table.Cell>
                                        <Table.Cell collapsing>
                                            {command.limit}/{command.interval}
                                        </Table.Cell>
                                        <Table.Cell collapsing>
                                            <UserDetailConfig config={command.config} />
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            }
                        </Table.Body>
                    </Table>
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Donation Config',
            render: () => (
                <Tab.Pane inverted style={{ padding: '0px', overflowX: 'hidden', overflowY: 'overlay', maxHeight: '350px' }}>
                    <Table inverted singleLine striped unstackable>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell collapsing>
                                    Paypal Email:
                                </Table.Cell>
                                <Table.Cell>
                                    {donationConfig.paypal_email || '[Empty]'}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell collapsing>
                                    Currency:
                                </Table.Cell>
                                <Table.Cell>
                                    {donationConfig.currency}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>
                                    Minimum:
                                </Table.Cell>
                                <Table.Cell>
                                    {donationConfig.minimum.toFixed(2)}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>
                                    Require Twitch:
                                </Table.Cell>
                                <Table.Cell>
                                    {donationConfig.twitch_required ?
                                        <Icon name='check' />
                                        :
                                        <Icon name='x' />
                                    }
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell verticalAlign='top'>
                                    Incentives:
                                </Table.Cell>
                                <Table.Cell>
                                    {donationConfig.incentives.length === 0 ?
                                        <p>No incentives configured.</p>
                                        :
                                        <Feed>
                                            {donationConfig.incentives.map(incentive => (
                                                <React.Fragment>
                                                    <Feed.Event>
                                                        <Feed.Content>
                                                            <Feed.Summary>
                                                                {incentive.description}
                                                            </Feed.Summary>
                                                            <Feed.Extra text>
                                                                <Table inverted style={{ paddingLeft: '25px' }}>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Command:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {incentive.command}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Params:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {incentive.command_params || '[Empty]'}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Minimum:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {incentive.minimum.toFixed(2)}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Enabled:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {incentive.enabled ?
                                                                                <Icon name='check' />
                                                                                :
                                                                                <Icon name='x' />
                                                                            }
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                </Table>
                                                            </Feed.Extra>
                                                        </Feed.Content>
                                                    </Feed.Event>
                                                    <Divider />
                                                </React.Fragment>
                                            ))}
                                        </Feed>
                                    }
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Channel Points',
            render: () => (
                <Tab.Pane inverted style={{ padding: '0px', overflowX: 'hidden', overflowY: 'overlay', maxHeight: '350px' }}>
                    <Table inverted singleLine striped unstackable>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell collapsing verticalAlign='top'>
                                    Redeems:
                                </Table.Cell>
                                <Table.Cell>
                                    {channelPoints.length === 0 ?
                                        <p>No channel point redemptions configured.</p>
                                        :
                                        <Feed>
                                            {channelPoints.map(redeem => (
                                                <React.Fragment>
                                                    <Feed.Event>
                                                        <Feed.Content>
                                                            <Feed.Summary>
                                                                Name: {redeem.name || "[No Name]"}<br />
                                                                Description: {redeem.description || "[No Description]"}
                                                            </Feed.Summary>
                                                            <Feed.Extra text>
                                                                <Table inverted style={{ paddingLeft: '25px' }}>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Channel Points:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.cost}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Command:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.command}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Params:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.params || '[Empty]'}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            User Input:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.input_required ?
                                                                                <Icon name='check' />
                                                                                :
                                                                                <Icon name='x' />
                                                                            }
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Maq Per Stream:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.max_per_stream}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Max Per User Per Stream:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.max_per_user_per_stream}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Global Cooldown:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.global_cooldown}
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    <Table.Row>
                                                                        <Table.Cell collapsing>
                                                                            Enabled:
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            {redeem.enabled ?
                                                                                <Icon name='check' />
                                                                                :
                                                                                <Icon name='x' />
                                                                            }
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                </Table>
                                                            </Feed.Extra>
                                                        </Feed.Content>
                                                    </Feed.Event>
                                                    <Divider />
                                                </React.Fragment>
                                            ))}
                                        </Feed>
                                    }
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Twitch Permissions',
            render: () => (
                <Tab.Pane inverted style={{ padding: '0px', overflowX: 'hidden', overflowY: 'overlay', maxHeight: '350px' }}>
                    <Table inverted singleLine striped unstackable>
                        <Table.Body>
                            {twitchPermissions.status !== 200 ?
                                <Table.Row>
                                    <Table.Cell collapsing>
                                        Error:
                                    </Table.Cell>
                                    <Table.Cell>
                                        There was an error with retrieving this users Twitch permissions:<br />
                                        <pre>{twitchPermissions.response.message}</pre>
                                    </Table.Cell>
                                </Table.Row>
                                :
                                <React.Fragment>
                                    <Table.Row>
                                        <Table.Cell collapsing>
                                            Twitch Login:
                                        </Table.Cell>
                                        <Table.Cell>
                                            {twitchPermissions.response.login}
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell collapsing>
                                            Twitch User ID:
                                        </Table.Cell>
                                        <Table.Cell>
                                            {twitchPermissions.response.user_id}
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            Token Expiration:
                                        </Table.Cell>
                                        <Table.Cell>
                                            {moment.utc(twitchPermissions.response.expires_in * 1000).format('HH:mm:ss')}
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell verticalAlign='top'>
                                            Token Scope:
                                        </Table.Cell>
                                        <Table.Cell onClick={startImpersonation}>
                                            {twitchPermissions.response.scopes.map((scope) => (
                                                <div>{scope}</div>
                                            ))}
                                        </Table.Cell>
                                    </Table.Row>
                                </React.Fragment>
                            }
                        </Table.Body>
                    </Table>
                </Tab.Pane>
            ),
        },
    ];

    return (
        <Modal
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            open={open}
            closeOnDimmerClick={false}
            trigger={
                <Button icon>
                    <Icon name='magnify' />
                </Button>
            }
        >
            <Modal.Header>User Details for {user.display_name}</Modal.Header>
            <Modal.Content>
                <Tab
                    menu={{ secondary: true, pointing: true, inverted: true }}
                    panes={panes}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button color='green' onClick={() => setOpen(false)}>
                    Close
                </Button>
            </Modal.Actions>
        </Modal>
    )
};

export default UserDetail;
